// @ts-check
// ── The cancellation paragraph a coach pastes into a client agreement ──
//
// ⚠️ THE PLACEHOLDERS USED TO EXPAND NOWHERE. {cancelEmail} was documented in
// the help text, saved with the template, and read by nothing. A coach who did
// exactly what the screen told them shipped the literal characters
// "cancel@{cancelEmail}" to their client, in a contract, as the address for
// cancelling. This is the renderer that was missing.
//
// ⚠️ AND THE OLD PARAGRAPH WAS NOT TRUE. It read as though the cancellation
// proceeds on its own. It does not. An inbound request writes a pending
// cancellation_saves row and notifies the coach; billing does not move until a
// coach clicks Cancel in the CRM, which is where the 30-day notice clock starts
// (cancel-client.js). That is the intended design — a request is a save
// opportunity, not an automatic cancellation — so the wording has to match it.
// A client who believes their subscription ended stops watching a card that is
// still being charged.
//
// ⚠️ LEGACY TEMPLATES. The shipped default said "email cancel@{cancelEmail}".
// {cancelEmail} is the WHOLE address now, so any saved copy still carrying that
// two-part form would render "cancel@cancel@mail...". The pair is collapsed as
// one token before the bare placeholder is looked at. Do not reorder those two.
//
// ⚠️ NO DASHES AS PUNCTUATION. This is client-facing copy that goes out over
// the coach's name. Use a period.
//
// ⚠️ PURE ON PURPOSE. It takes the address as a value rather than reaching for
// buildCancelAddress itself, so it can be unit tested without a settings object
// and there is exactly one place (renderCancelAgreementPreview in
// modules/settings.js) that decides which address a coach actually has.

/** The paragraph a coach starts from. Also the fallback when nothing is saved. */
export const DEFAULT_CANCEL_AGREEMENT_TEMPLATE =
  'To cancel your coaching, email {cancelEmail}. Your request reaches your coach right away, and they will follow up with you personally. Nothing is cancelled automatically. Your coach will invite you to a 15-minute exit interview. You\'ll look at where you started, where you are now and how to keep making progress.';

/** Optional inner whitespace, because a coach will type `{ cancelEmail }`. */
const token = (name) => new RegExp(`\\{\\s*${name}\\s*\\}`, 'g');

/** The legacy two-part form. MUST be collapsed before the bare placeholder. */
const LEGACY_PREFIXED_CANCEL = /cancel@\s*\{\s*cancelEmail\s*\}/g;

/**
 * Expand the placeholders in a coach's cancellation paragraph.
 *
 * @param {string|null|undefined} template
 * @param {{cancelEmail?: string, coachName?: string, businessName?: string}|null|undefined} ctx
 * @returns {string} the finished paragraph, with any placeholder it cannot fill left visible
 */
export function renderCancelAgreement(template, ctx) {
  let out = String(template ?? '');
  if (!out) return '';
  const c = ctx || {};

  // Order is load-bearing. See the header.
  if (c.cancelEmail) {
    out = out.replace(LEGACY_PREFIXED_CANCEL, c.cancelEmail);
    out = out.replace(token('cancelEmail'), c.cancelEmail);
  }
  // An unfilled placeholder is LEFT IN PLACE on purpose. Blanking it produces a
  // sentence that reads fine and names no address, which is worse than a
  // visible {cancelEmail} the coach can see and ask about.
  if (c.coachName) out = out.replace(token('coachName'), c.coachName);
  if (c.businessName) out = out.replace(token('businessName'), c.businessName);
  return out;
}

// ── Change billing round two, piece 2a: accounts with Change billing on ──────────────────────────────────────────────
// Their client's cancel email starts the 30 days' notice by itself (netlify/functions/_shared/cancel-email-notice.js),
// so "Nothing is cancelled automatically." is no longer true for them. Clayton's words, approved 10/8.

/** The notice sentences that take the place of "Nothing is cancelled automatically." */
const NOTICE_SENTENCES = "Your 30 days' notice starts the day your email arrives, and you'll get a reply confirming it. A payment that comes due during those 30 days still goes through, and your coaching runs to the end of the time that payment covers.";

/** The paragraph a Change billing account starts from. */
export const CHANGE_BILLING_CANCEL_AGREEMENT_TEMPLATE =
  `To cancel your coaching, email {cancelEmail}. ${NOTICE_SENTENCES} Before your last day, your coach will invite you to a 15-minute exit interview. You'll look at where you started, where you are now and how to keep making progress.`;

/**
 * The paragraph to show: with Change billing off, the saved one or the default, as before; with it on, the Change
 * billing paragraph when nothing is saved or the saved one is the default, else the coach's own words with only
 * "Nothing is cancelled automatically." swapped for the notice sentences.
 * @param {string|null|undefined} saved @param {{ changeBillingOn: boolean }} o
 * @returns {string}
 */
export function cancelAgreementFor(saved, { changeBillingOn }) {
  if (!changeBillingOn) return saved || DEFAULT_CANCEL_AGREEMENT_TEMPLATE;
  if (!saved || saved === DEFAULT_CANCEL_AGREEMENT_TEMPLATE) return CHANGE_BILLING_CANCEL_AGREEMENT_TEMPLATE;
  return saved.replace('Nothing is cancelled automatically.', NOTICE_SENTENCES);
}

/**
 * Settings asks the server once (ask) whether the account has Change billing on, and swaps the box when it does. A box
 * the coach started editing while the answer was on its way is left alone.
 * @param {{ box: { value: string }|null, saved: string|null|undefined, ask: () => Promise<any> }} a
 * @returns {Promise<boolean>} true when the box was swapped
 */
export async function useChangeBillingParagraph({ box, saved, ask }) {
  if (!box) return false;
  const shown = box.value;
  let enabled = false;
  try { const res = await ask(); enabled = !!(res && res.enabled); } catch { return false; }
  if (!enabled || box.value !== shown) return false;
  box.value = cancelAgreementFor(saved, { changeBillingOn: true });
  return true;
}
